import { Suspense } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import styled from 'styled-components';

import { AppDispatch } from 'redux-types';
import { fullLogout, logout } from '@redux/reducers/auth/action';

import Box from '@components/atoms/Box/Box';
import Button from '@components/atoms/Button/Button';
import Loader from '@components/atoms/Loader/Loader';
import Text from '@components/atoms/Text/Text';
import CenterMessage from '@components/organisms/CenterMessage';

import { isCN } from '@utils/origin';

import { Size } from '@shared/enums/button-size';
import { breakpoint } from 'theme';

type SignOutPopupProps = {
  handleCloseSignOutPopup: () => void;
  dispatch: AppDispatch;
  isDiscount?: boolean;
};

function SignOutPopup(props: SignOutPopupProps) {
  const { handleCloseSignOutPopup, dispatch } = props;
  const { t } = useTranslation('account', { useSuspense: true });

  const handleLogout = () => {
    dispatch(logout());
  };

  const handleFullLogout = () => {
    dispatch(fullLogout());
  };

  return (
    <CenterMessage onClose={handleCloseSignOutPopup} isSignOut>
      <SignOutBox>
        <TitleBox mb={1}>
          <Text smSize={2} size={2.25} weight="bold" lineHeight="normal">
            {t('signOutTitle')}
          </Text>
        </TitleBox>
        <Box>
          <Trans
            t={t}
            i18nKey="signOutWarning"
            smSize={1.25}
            size={1.25}
            lineHeight={1.5}
            accountType={isCN ? '3D4Medical' : 'Elsevier'}
          />
          <ManageAccountBox>
            <SignOutButton sizeType={Size.Ninth} onClick={handleLogout}>
              {t('storeSignOut')}
            </SignOutButton>
            <Box ml={0.5} mr={0.5} />
            <ManageAccountButton
              sizeType={Size.Ninth}
              onClick={handleFullLogout}
            >
              {t('manageAccount')}
            </ManageAccountButton>
          </ManageAccountBox>
        </Box>
      </SignOutBox>
    </CenterMessage>
  );
}

const TitleBox = styled(Box)`
  text-align: left;
  width: 50%;
  margin-bottom: 5em;

  ${breakpoint.sm`
    margin-bottom: 1em;
  `}
`;

const SignOutBox = styled(Box)`
  display: flex;
  flex-direction: column;

  ${breakpoint.xs`
    margin-top: 0.5em
  `}
`;

const ManageAccountBox = styled(Box)`
  margin: 1.25em auto 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  flex-grow: 1;

  ${breakpoint.sm`
    flex-direction: row;
  `}
`;

const SignOutButton = styled(Button)`
  width: 15em;
  height: 2.5em;
  color: ${({ theme }) => theme.colors.abyss};
  border: 1px solid ${props => props.theme.colors.abyss};
  font-weight: 700;
  margin-bottom: 1em;
`;

const ManageAccountButton = styled(Button)`
  width: 15em;
  height: 2.5em;
  color: white;
  background-color: ${({ theme }) => theme.colors.abyss};
  font-weight: 700;
  margin-bottom: 1em;
`;

const SignOutPopupWithSuspense = (props: SignOutPopupProps) => (
  <Suspense fallback={<Loader />}>
    <SignOutPopup {...props} />
  </Suspense>
);

export default SignOutPopupWithSuspense;
